import React, { useState } from 'react';
import { db, doc, setDoc, serverTimestamp } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  makeShareToken,
  formatTimeLeft,
  msUntilExpiry,
} from '../utils/shareTokens';
import {
  X, Link as LinkIcon, Copy, Check, Share2, Loader2, Sparkles,
} from 'lucide-react';

/**
 * Modal that generates a public pay-link for a maintenance routine.
 * Writes a snapshot to shareLinks/{token} and shows the URL.
 */
const SharePaymentLinkModal = ({ schedule, onClose }) => {
  const { user, userData } = useAuth();

  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const buildUrl = (token) =>
    `${window.location.origin}/maintenance/${user.uid}/${schedule.id}?token=${token}`;

  const handleGenerate = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const tokenPayload = makeShareToken(72);
      const shareDoc = {
        token: tokenPayload.token,
        type: 'maintenance',
        ownerUid: user.uid,
        ownerName: userData?.name || 'A Skytone user',
        ownerImage: userData?.profileImage || null,
        scheduleId: schedule.id,
        routineTitle: schedule.title || `${schedule.category} routine`,
        category: schedule.category || null,
        amount: Number(schedule.price || 0),
        preferredProName: schedule.preferredProName || null,
        createdAt: serverTimestamp(),
        expiresAt: tokenPayload.expiresAt,
        paid: false,
        paidBy: null,
        appliedToSchedule: false,
      };
      await setDoc(doc(db, 'shareLinks', tokenPayload.token), shareDoc);
      setShareUrl(buildUrl(tokenPayload.token));
      setExpiresAt(tokenPayload.expiresAt);
    } catch (err) {
      console.error('Failed to create pay link:', err);
      setError('Could not create the link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Help me with my routine',
          text: `Help me pay for my "${schedule.title || schedule.category}" routine on Skytone 💫`,
          url: shareUrl,
        });
      } catch (e) { /* cancelled */ }
    } else {
      handleCopy();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif font-bold text-xl text-gray-900">
            Ask someone to pay
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        {/* Routine summary */}
        <div className="bg-[#FCF8F9] rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-[#EFA9B6]" />
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Routine
            </span>
          </div>
          <p className="font-bold text-gray-900 truncate">
            {schedule.title || `${schedule.category} routine`}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            R{Number(schedule.price || 0).toFixed(2)}
            {schedule.preferredProName && ` · ${schedule.preferredProName}`}
          </p>
        </div>

        {!shareUrl ? (
          <>
            <p className="text-sm text-gray-500 mb-5">
              Generate a link you can send to anyone. They can pay for this routine — no Skytone account required.
            </p>

            {error && (
              <div className="mb-4 px-3 py-2 rounded-xl bg-red-50 text-xs text-red-600">
                {error}
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-3.5 rounded-full bg-[#EFA9B6] text-white font-bold text-sm shadow-lg hover:bg-[#EFA9B6]/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <LinkIcon size={16} />
                  Generate pay link
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Share this link. Anyone who opens it can pay R{Number(schedule.price || 0).toFixed(2)} toward this routine.
            </p>

            <div className="bg-[#FCF8F9] rounded-2xl p-3 flex items-center gap-2 mb-4">
              <LinkIcon size={16} className="text-[#EFA9B6] shrink-0" />
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="bg-transparent border-none outline-none flex-1 text-xs text-gray-700 font-mono truncate"
              />
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={handleCopy}
                className="flex-1 py-3 rounded-full bg-[#FFF5F6] text-[#EFA9B6] font-bold text-sm hover:bg-[#FBE1E8] transition-colors flex items-center justify-center gap-2"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              <button
                onClick={handleNativeShare}
                className="flex-1 py-3 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
              >
                <Share2 size={16} />
                Share
              </button>
            </div>

            {expiresAt && (
              <p className="text-center text-[11px] text-gray-400">
                Link expires in {formatTimeLeft(msUntilExpiry({ expiresAt }))}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SharePaymentLinkModal;